import React from 'react';
import { motion } from 'framer-motion';
import {
    Heart,
    Users,
    Leaf,
    ArrowRight,
    ChevronRight,
    Mail,
    Linkedin,
    Phone
} from 'lucide-react';

import hamudurwo from '../assets/hamuduruwo.jpeg';
import drmanike from '../assets/drmanike.jpeg';
import hiruni from '../assets/hiruni.jpeg';
import kostha from '../assets/kostha.jpg';
import sithum from '../assets/sithum.jpg';
import vimukthi from '../assets/vimukthi.jpg';
import batu from '../assets/batu.png';
import chaya from '../assets/chaya.png';
import shanaka from '../assets/shanaka.png';
import saku from '../assets/sakuakka.png';
import up from "../assets/up.png";
import down from "../assets/down.png";
import com from "../assets/com.jpg";

export default function OurCommunity() {
    // BRAND COLORS
    const darkGreen = "#1A2E20";
    const goldAccent = "#D4AF37";
    const creamBg = "#FDFBF7";

    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";
    const heroImage = com;

    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-50px" },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    // Exactly 3 healers
    const healers = [
        { name: "Mr. Sithum Jayasekara", specialty: "Reiki Master", image: sithum },
        { name: "Mr. Chamal Rasitha Costa", specialty: "Reiki Master", image: kostha },
        { name: "Mr. Vimukthi Lakshan", specialty: "Reiki Master", image: vimukthi }
    ];

    const staffMembers = [
        { name: "Miss. Chaya Dewasmitha", role: "Senior Executive", image: chaya },
        { name: "Mr. Shanaka Indumal", role: "Assistant Librarian", image: shanaka },
        { name: "Miss. Nethmi Hiruni", role: "Junior Executive", image: batu },
        { name: "Miss. Sakunthala", role: "Patient Coordinator", image: saku }
    ];

    const volunteersRow1 = [
        { name: "Saman Kumara", dedication: "" },
        { name: "Priyanka Dias", dedication: "" },
        { name: "Kamal Perera", dedication: "" }
    ];

    const volunteersRow2 = [
        { name: "Nuwan & Family", dedication: "" },
        { name: "Ashan Herath", dedication: "" },
        { name: "Shanthi Silva", dedication: "" }
    ];

    // Duplicated to create the infinite loop effect
    const slidingRow1 = [...volunteersRow1, ...volunteersRow1, ...volunteersRow1];
    const slidingRow2 = [...volunteersRow2, ...volunteersRow2, ...volunteersRow2];

    return (
        <div style={{ backgroundColor: creamBg, color: darkGreen }} className="min-h-screen font-sans selection:bg-[#D4AF37] selection:text-white relative overflow-hidden">
            {/* Background Texture Overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-40 z-10" style={{ backgroundImage: `url(${bgPattern})` }}></div>

            {/* HERO SECTION */}
            <section className="relative h-[60vh] md:h-[65vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img src={heroImage} className="w-full h-full object-cover" alt="Boseth Community" />
                    <div className="absolute inset-0 bg-[#1A2E20]/60 backdrop-blur-[1px]"></div>
                </div>
                <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-20">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                        <div className="flex items-center gap-2 text-white/90 text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-4 sm:mb-6 font-bold">
                            <span>Home</span>
                            <ChevronRight size={12} style={{ color: goldAccent }} />
                            <span className="text-white font-medium">Our People</span>
                        </div>
                        <h1 className="font-serif text-4xl sm:text-6xl md:text-8xl text-white leading-tight">
                            The Hands Behind <br/>
                            <span className="italic font-light" style={{ color: goldAccent }}>The Healing</span>
                        </h1>
                        <p className="mt-6 sm:mt-8 text-white/80 text-xs sm:text-base md:text-xl font-light tracking-widest uppercase max-w-2xl">
                            Dedicated physicians, staff, and volunteers preserving traditional wellness.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* SACRED SANCTUARY INTRO SECTION */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6 z-20">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div {...fadeIn}>
                        <div className="flex justify-center mb-4 sm:mb-6">
                            <img src={up} alt="Flourish Top" className="w-auto h-12 sm:h-16 md:h-20 object-contain" />
                        </div>
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block mb-4 sm:mb-6">
                            A Sacred Sanctuary
                        </span>
                        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl mb-6 sm:mb-8 leading-tight" style={{ color: darkGreen }}>
                            A Haven for Collective Healing
                        </h2>
                        <p className="text-gray-600 text-sm sm:text-base md:text-lg font-light leading-relaxed mb-8 sm:mb-10 max-w-3xl mx-auto">
                            You can put your burdens down here. You are safe, you won't be judged. Together, we share the knowledge, habits, and support needed to actively build a healed soul and a body. Healing happens while in connectivity to others. We are a collective growing toward wholeness and heal together.
                        </p>
                        <div className="flex justify-center mt-4 sm:mt-6">
                            <img src={down} alt="Divider Bottom" className="w-auto h-2 sm:h-3 md:h-4 object-contain" />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* THE HEALERS SECTION */}
            <section className="relative pb-20 sm:pb-32 px-4 sm:px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-12 sm:mb-16 text-center">
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block mb-2 sm:mb-4 flex items-center justify-center gap-2 sm:gap-3">
                            <Leaf size={14} /> Core Pillar
                        </span>
                        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl" style={{ color: darkGreen }}>Our Healers</h2>
                        <div className="h-1 w-16 sm:w-20 mx-auto mt-4 sm:mt-6" style={{ backgroundColor: goldAccent }}></div>
                    </div>

                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-8 lg:gap-12 max-w-5xl mx-auto">
                        {healers.map((doc, i) => (
                            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }} className="group text-center">
                                <div className="relative aspect-[4/5] max-w-[290px] sm:max-w-[340px] md:max-w-full mx-auto overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] mb-4 sm:mb-8 shadow-2xl">
                                    <img src={doc.image} alt={doc.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                                    <div className="absolute inset-0 via-transparent to-transparent opacity-0 group-hover:opacity-90 transition-opacity duration-500 flex items-end justify-center pb-6 sm:pb-8" style={{ background: `linear-gradient(to top, ${darkGreen}, transparent)` }}>
                                        <div className="flex gap-4">
                                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer"><Mail size={14} className="text-white sm:w-4 sm:h-4" /></div>
                                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer"><Phone size={14} className="text-white sm:w-4 sm:h-4" /></div>
                                        </div>
                                    </div>
                                </div>
                                <h3 className="font-serif text-xl sm:text-2xl mb-1 sm:mb-2 max-w-[280px] mx-auto" style={{ color: darkGreen }}>{doc.name}</h3>
                                <p style={{ color: goldAccent }} className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.25em] sm:tracking-[0.3em]">{doc.specialty}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FOUNDATION STAFF SECTION */}
            <section className="relative py-16 sm:py-24 bg-[#1A2E20]/5 px-4 sm:px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block mb-2 sm:mb-4 flex items-center justify-center gap-2 sm:gap-3">
                            <Users size={14} /> Administration
                        </span>
                        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl" style={{ color: darkGreen }}>Foundation Staff</h2>
                        <div className="h-1 w-16 sm:w-20 mx-auto mt-4 sm:mt-6" style={{ backgroundColor: goldAccent }}></div>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-md sm:max-w-3xl lg:max-w-7xl mx-auto">
                        {staffMembers.map((staff, i) => (
                            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }} className="bg-white p-5 sm:p-6 rounded-[1.5rem] sm:rounded-[2rem] shadow-lg border border-[#E5E0D8] hover:-translate-y-2 transition-transform duration-300 flex flex-col justify-between max-w-[290px] sm:max-w-full mx-auto w-full">
                                <div>
                                    {/* Enforced uniform aspect ratio, max-widths, and auto-centering */}
                                    <div className="aspect-square max-w-[240px] sm:max-w-[280px] md:max-w-full mx-auto rounded-xl sm:rounded-2xl overflow-hidden mb-4 sm:mb-6 relative shadow-sm bg-gray-50">
                                        <img src={staff.image} alt={staff.name} className="w-full h-full object-cover object-center" />
                                    </div>
                                    <h4 className="font-serif text-lg sm:text-xl mb-1 text-center" style={{ color: darkGreen }}>{staff.name}</h4>
                                    <p className="text-gray-500 text-xs sm:text-sm font-light mb-4 text-center">{staff.role}</p>
                                </div>
                                <div className="flex gap-2 justify-center">
                                    <button className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-gray-50 flex items-center justify-center hover:bg-[#D4AF37] hover:text-white transition-colors text-gray-400">
                                        <Linkedin size={12} className="sm:w-3.5 sm:h-3.5" />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* VOLUNTEERS SECTION */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-3 gap-10 lg:gap-12 items-center">
                        <motion.div {...fadeIn} className="lg:col-span-1 space-y-4 sm:space-y-6 text-center lg:text-left">
                            <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block">The Heartbeat</span>
                            <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl leading-tight" style={{ color: darkGreen }}>
                                Our Noble <br />
                                <span className="italic font-light" style={{ color: goldAccent }}>Volunteers</span>
                            </h2>
                            <p className="text-gray-600 text-sm sm:text-base font-light leading-relaxed max-w-xl mx-auto lg:mx-0">
                                As a non-profit organization, we rely on the pure intentions and hard work of our community volunteers. They are the silent heroes who keep our mission thriving.
                            </p>
                        </motion.div>

                        {/* Auto-Sliding Rows */}
                        <div className="lg:col-span-2 relative overflow-hidden flex flex-col gap-3 sm:gap-4 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-12 sm:before:w-16 before:bg-gradient-to-r before:from-[#FDFBF7] before:to-transparent before:z-10 after:absolute after:right-0 after:top-0 after:bottom-0 after:w-12 sm:after:w-16 after:bg-gradient-to-l after:from-[#FDFBF7] after:to-transparent after:z-10">

                            {/* Row 1 - Slides Left */}
                            <motion.div
                                className="flex gap-3 sm:gap-4 py-1"
                                animate={{ x: [0, "-33.33%"] }}
                                transition={{ ease: "linear", duration: 15, repeat: Infinity }}
                                style={{ width: "max-content" }}
                            >
                                {slidingRow1.map((vol, i) => (
                                    <div key={i} className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl border border-gray-100 shadow-sm min-w-[240px] sm:min-w-[280px]">
                                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${goldAccent}1A` }}>
                                            <Heart color={goldAccent} size={18} className="sm:w-5 sm:h-5" />
                                        </div>
                                        <div>
                                            <h4 className="font-serif text-base sm:text-lg font-medium" style={{ color: darkGreen }}>{vol.name}</h4>
                                            <p className="text-xs sm:text-sm text-gray-500 font-light">{vol.dedication}</p>
                                        </div>
                                    </div>
                                ))}
                            </motion.div>

                            {/* Row 2 - Slides Right */}
                            <motion.div
                                className="flex gap-3 sm:gap-4 py-1"
                                animate={{ x: ["-33.33%", 0] }}
                                transition={{ ease: "linear", duration: 15, repeat: Infinity }}
                                style={{ width: "max-content" }}
                            >
                                {slidingRow2.map((vol, i) => (
                                    <div key={i} className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl border border-gray-100 shadow-sm min-w-[240px] sm:min-w-[280px]">
                                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${goldAccent}1A` }}>
                                            <Heart color={goldAccent} size={18} className="sm:w-5 sm:h-5" />
                                        </div>
                                        <div>
                                            <h4 className="font-serif text-base sm:text-lg font-medium" style={{ color: darkGreen }}>{vol.name}</h4>
                                            <p className="text-xs sm:text-sm text-gray-500 font-light">{vol.dedication}</p>
                                        </div>
                                    </div>
                                ))}
                            </motion.div>

                        </div>
                    </div>
                </div>
            </section>

            {/* CALL TO ACTION */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        {...fadeIn}
                        className="bg-white border border-[#E5E0D8] rounded-[2rem] sm:rounded-[3rem] p-8 sm:p-12 md:p-20 text-center shadow-2xl relative overflow-hidden"
                    >
                        {/* Decorative Background Pattern */}
                        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `url(${bgPattern})` }}></div>

                        <div className="relative z-10">
                            <Heart color={goldAccent} size={36} className="mx-auto mb-6 sm:mb-8 sm:w-10 sm:h-10" />
                            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-4 sm:mb-6" style={{ color: darkGreen }}>Want to give back?</h2>
                            <p className="text-gray-600 text-sm sm:text-base md:text-lg font-light max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">
                                Join our community of volunteers. Whether you have specific skills or just a willing heart, there is a place for you here.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
                                <button className="w-full sm:w-auto text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full font-bold uppercase text-[11px] sm:text-[12px] tracking-[0.2em] hover:bg-[#D4AF37] transition-all flex items-center justify-center gap-3 group shadow-lg" style={{ backgroundColor: darkGreen }}>
                                    Become a Volunteer <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button
                                    className="w-full sm:w-auto bg-transparent border-2 px-8 sm:px-10 py-4 sm:py-5 rounded-full font-bold uppercase text-[11px] sm:text-[12px] tracking-[0.2em] transition-all duration-300 hover:bg-[#1A2E20] hover:text-white"
                                    style={{ borderColor: darkGreen, color: darkGreen }}
                                >
                                    Contact Us
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}